/**
 * POS-I205: the Store Guide keeps its tab, search text, category chip and open card(s) in the URL so a
 * reload restores them and support can send a link. Every value read from the URL is validated against
 * the known tab, chip and card ids; anything else falls back to the default and is never used as a
 * selector (callers use getElementById on a known id only).
 */

export type GuideTab = 'wizard' | 'manual' | 'fbr-scenarios';

/** The value written to ?tab= for each tab (the TabBar keys differ for the FBR tab). */
export const TAB_PARAM: Record<GuideTab, string> = {
  wizard: 'wizard',
  manual: 'manual',
  'fbr-scenarios': 'fbr',
};

export function tabFromParam(value: string | null): GuideTab {
  if (value === 'manual') return 'manual';
  if (value === 'fbr' || value === 'fbr-scenarios') return 'fbr-scenarios';
  return 'wizard';
}

const MAX_QUERY = 100;

/** Search text from ?q= : free text, only length-limited (it is only ever used as a filter string). */
export function cleanQuery(value: string | null): string {
  return (value ?? '').slice(0, MAX_QUERY);
}

/** A chip id from ?cat= ; unknown values give the fallback. */
export function chipFromParam<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.find((a) => a === value) ?? fallback;
}

/** Open card ids from ?card= (comma separated); unknown ids are dropped, duplicates removed. */
export function cardsFromParam(value: string | null, known: readonly string[]): string[] {
  if (!value) return [];
  const out: string[] = [];
  for (const part of value.split(',')) {
    if (known.includes(part) && !out.includes(part)) out.push(part);
  }
  return out;
}

/** The "Copy link" URL: current origin and path plus the validated tab and card only. */
export function buildCardLink(origin: string, pathname: string, tab: GuideTab, cardId: string): string {
  const params = new URLSearchParams({ tab: TAB_PARAM[tab], card: cardId });
  return `${origin}${pathname}?${params.toString()}`;
}
